import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, User, Send, Loader2, Facebook, Instagram } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { FadeIn, SectionHeader } from "./Reveal";
import { api, formatApiErrorDetail } from "../../lib/api";
import { WhatsAppIcon, WHATSAPP_URL } from "./WhatsApp";

const CONTACT = [
    { icon: Phone, label: "Telefon", value: "+48 512 913 717", href: "tel:+48512913717", testid: "contact-phone" },
    { icon: Mail, label: "Email", value: "handyskrakow@gmail.com", href: "mailto:handyskrakow@gmail.com", testid: "contact-email" },
    { icon: User, label: "Właściciel", value: "Jan Białoń", href: null, testid: "contact-owner" },
];

export default function Contact() {
    const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
    const [sending, setSending] = useState(false);

    const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await api.post("/leads", form);
            toast.success("Dziękujemy za zgłoszenie!", {
                description: "Oddzwonimy w ciągu 24 godzin i umówimy darmową wycenę.",
            });
            setForm({ name: "", phone: "", email: "", message: "" });
        } catch (err) {
            toast.error("Nie udało się wysłać zgłoszenia", {
                description: formatApiErrorDetail(err.response?.data?.detail),
            });
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="kontakt" className="bg-white py-24 md:py-32">
            <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 md:px-12 lg:grid-cols-2">
                <div>
                    <SectionHeader index="07" eyebrow="Kontakt" title="Porozmawiajmy o Twoim projekcie." />
                    <FadeIn delay={0.15}>
                        <p className="mt-8 max-w-md text-base leading-relaxed text-ink/60">
                            Opowiedz nam o swoim zleceniu — doradzimy, wycenimy i znajdziemy
                            najlepszy termin. Bez zobowiązań.
                        </p>
                    </FadeIn>
                    <div className="mt-10 space-y-4">
                        {CONTACT.map((c, i) => (
                            <FadeIn key={c.label} delay={0.2 + i * 0.07}>
                                {c.href ? (
                                    <a
                                        data-testid={c.testid}
                                        href={c.href}
                                        className="group flex items-center gap-5 rounded-2xl border border-ink/8 bg-white p-5 transition-[border-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:border-gold/60 hover:shadow-lg"
                                    >
                                        <span className="rounded-xl bg-ink p-3 text-gold transition-[background-color,color] duration-300 group-hover:bg-gold group-hover:text-ink">
                                            <c.icon className="h-5 w-5" />
                                        </span>
                                        <span>
                                            <span className="block text-xs font-semibold uppercase tracking-[0.2em] text-ink/40">{c.label}</span>
                                            <span className="block font-display text-lg font-bold text-ink">{c.value}</span>
                                        </span>
                                    </a>
                                ) : (
                                    <div data-testid={c.testid} className="flex items-center gap-5 rounded-2xl border border-ink/8 bg-white p-5">
                                        <span className="rounded-xl bg-ink p-3 text-gold">
                                            <c.icon className="h-5 w-5" />
                                        </span>
                                        <span>
                                            <span className="block text-xs font-semibold uppercase tracking-[0.2em] text-ink/40">{c.label}</span>
                                            <span className="block font-display text-lg font-bold text-ink">{c.value}</span>
                                        </span>
                                    </div>
                                )}
                            </FadeIn>
                        ))}
                        <FadeIn delay={0.45}>
                            <a
                                data-testid="contact-whatsapp-btn"
                                href={WHATSAPP_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center gap-3 rounded-full bg-[#25D366] px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-white shadow-[0_12px_30px_-10px_rgba(37,211,102,0.6)] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-10px_rgba(37,211,102,0.7)]"
                            >
                                <WhatsAppIcon className="h-5 w-5" />
                                Napisz na WhatsApp
                            </a>
                        </FadeIn>
                        <FadeIn delay={0.52}>
                            <div className="flex items-center justify-center gap-3 pt-1">
                                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-ink/40">Obserwuj nas</span>
                                <a
                                    data-testid="contact-facebook-btn"
                                    href="https://www.facebook.com/share/19XGcWK2LS/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Handy's na Facebooku"
                                    className="rounded-full border border-ink/10 p-3 text-ink/60 transition-[background-color,border-color,color] duration-300 hover:border-gold hover:bg-gold hover:text-ink"
                                >
                                    <Facebook className="h-4 w-4" />
                                </a>
                                <a
                                    data-testid="contact-instagram-btn"
                                    href="https://www.instagram.com/handys_krakow"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Handy's na Instagramie"
                                    className="rounded-full border border-ink/10 p-3 text-ink/60 transition-[background-color,border-color,color] duration-300 hover:border-gold hover:bg-gold hover:text-ink"
                                >
                                    <Instagram className="h-4 w-4" />
                                </a>
                            </div>
                        </FadeIn>
                    </div>
                </div>

                <FadeIn delay={0.2} y={40}>
                    <form
                        data-testid="contact-form"
                        onSubmit={submit}
                        className="rounded-[2rem] border border-ink/8 bg-mist p-8 shadow-sm md:p-10"
                    >
                        <h3 className="font-display text-2xl font-bold tracking-tight text-ink">Wyślij zgłoszenie</h3>
                        <p className="mt-2 text-sm text-ink/50">Odpowiadamy w ciągu 24 godzin.</p>
                        <div className="mt-8 space-y-5">
                            <div>
                                <label htmlFor="cf-name" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-ink/50">Imię</label>
                                <Input id="cf-name" data-testid="contact-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder="Twoje imię" className="h-12 rounded-xl border-ink/10 bg-white focus-visible:ring-gold" />
                            </div>
                            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                                <div>
                                    <label htmlFor="cf-phone" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-ink/50">Telefon</label>
                                    <Input id="cf-phone" data-testid="contact-phone-input" required minLength={7} type="tel" value={form.phone} onChange={set("phone")} placeholder="+48 ..." className="h-12 rounded-xl border-ink/10 bg-white focus-visible:ring-gold" />
                                </div>
                                <div>
                                    <label htmlFor="cf-email" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-ink/50">Email</label>
                                    <Input id="cf-email" data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="ty@email.pl" className="h-12 rounded-xl border-ink/10 bg-white focus-visible:ring-gold" />
                                </div>
                            </div>
                            <div>
                                <label htmlFor="cf-message" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-ink/50">Opis zlecenia</label>
                                <Textarea id="cf-message" data-testid="contact-message-input" required minLength={5} rows={5} value={form.message} onChange={set("message")} placeholder="Opisz krótko, co mamy zrobić — np. remont łazienki 5 m², wymiana instalacji..." className="resize-none rounded-xl border-ink/10 bg-white focus-visible:ring-gold" />
                            </div>
                            <button
                                data-testid="contact-submit-btn"
                                type="submit"
                                disabled={sending}
                                className="group flex w-full items-center justify-center gap-2 rounded-full bg-ink py-4 font-display text-sm font-bold uppercase tracking-wider text-white transition-[background-color,color,transform] duration-300 hover:-translate-y-0.5 hover:bg-gold hover:text-ink disabled:opacity-60"
                            >
                                {sending ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" /> Wysyłanie...
                                    </>
                                ) : (
                                    <>
                                        Wyślij <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </FadeIn>
            </div>
        </section>
    );
}
